import { json, useLoaderData, useNavigate, useParams } from "@remix-run/react";
import { useContext } from "react";
import PageWrapper from "~/components/PageWrapper/PageWrapper";
import ThemeContext from "~/contexts/ThemeContext";
import { urlGen } from "~/helpers/routesHelpers";
import { SiteListObject } from "~/entities/siteEntities";
import SitesList from "~/modules/sites/components/SitesList/SitesList";
import Api from "~/api/api";
import { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import OrderContext from "~/contexts/OrderContext";
import BaseLayout from "~/layouts/BaseLayout/BaseLayout";
import { iconMeta } from "~/helpers/remixHelpers";
import { getPersistentSession } from "~/sessions";

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  return [
    iconMeta(matches),
    {
      title: `${
        data?.orgSites?.domainSettings?.title
          ? data?.orgSites?.domainSettings?.title
          : data?.orgSites?.orgName
      } - Sites`,
    },
    {
      name: "description",
      content: data?.orgSites?.domainSettings?.description
        ? `${data?.orgSites?.domainSettings?.description} - Choosing a site for organisation ${data?.orgSites?.orgName}`
        : `Choosing a site for organisation ${data?.orgSites?.orgName}`,
    },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const orgSites = await Api.Site.getOrgSites(request);

  const persistentSession = await getPersistentSession(
    request.headers.get("Cookie"),
  );
  let prevSiteId = persistentSession.get("prevSite");

  if (!orgSites.sites.find((x) => x.id === prevSiteId)) {
    prevSiteId = undefined;
  }

  return json({
    orgSites,
    prevSiteId,
  });
}

const Page = () => {
  const { orgSites, prevSiteId } = useLoaderData<typeof loader>();

  const { template } = useContext(ThemeContext);
  const { orderSource } = useContext(OrderContext);

  const navigate = useNavigate();

  const params = useParams();

  const goToOrder = (site: SiteListObject, orderTypeCode?: string | null) => {
    navigate(
      urlGen("Order", {
        orderSource,
        siteId: site.id,
        tableNumber: params.table,
        orderTypeCode: orderTypeCode,
      }),
    );
  };

  // Style
  const backgroundColor = template.storesScreen.general.backgroundColour;

  return (
    <BaseLayout orgId={orgSites.id}>
      <PageWrapper
        loading={false}
        style={{ backgroundColor, padding: "0 16px" }}
      >
        <SitesList
          sites={orgSites.sites}
          onSelectSite={goToOrder}
          prevSiteId={prevSiteId}
        />
      </PageWrapper>
    </BaseLayout>
  );
};

export default Page;
